/**
 * The one tab strip both invoice screens wear.
 *
 * Recurring is not a STATE — it is the sibling screen holding the schedules
 * that write the retainer drafts — but readers look for it beside Paid and
 * Void, not in a header button (Jesse, 2026-08-22). So the strip carries it as
 * a navigation tab: picking it changes route, picking a state on the recurring
 * screen navigates back. One strip, one place to look, and the URL still says
 * where you are.
 */
export type StateTab = 'ALL' | 'DRAFT' | 'OPEN' | 'LATE' | 'PARTIAL' | 'PAID' | 'VOID';

/** The strip value for the recurring screen — never a ?state= value. */
export const RECURRING_TAB = 'RECURRING' as const;

/**
 * Jesse, 2026-10-01: *"we never have partial invoice payments… re-order this to:
 * Open (include draft and partials), Late, Draft, Paid, Void, All, Recurring.
 * Make sure the draft invoices are clearly unsent in the open tab."* So Open is
 * everything not yet paid — drafts included, each marked NOT SENT and kept out
 * of the amount owed — and Partial is no longer a tab (an old `?state=PARTIAL`
 * link lands on Open, which holds those invoices too).
 */
export const STATE_TABS: Array<{ value: StateTab; label: string; title?: string }> = [
  { value: 'OPEN', label: 'Open', title: 'Everything not yet paid — drafts not sent yet, and invoices sent and still owed' },
  { value: 'LATE', label: 'Late', title: 'Owed and past its due date' },
  { value: 'DRAFT', label: 'Draft', title: 'Written, not yet sent to the client' },
  { value: 'PAID', label: 'Paid', title: 'Settled in full' },
  { value: 'VOID', label: 'Void', title: 'Cancelled — kept for the record' },
  { value: 'ALL', label: 'All', title: 'Every invoice ever raised' },
];

export const STRIP_TABS = [
  ...STATE_TABS,
  {
    value: RECURRING_TAB,
    label: 'Recurring',
    title: 'Schedules that draft an invoice for you each period — a screen, not a state filter',
  },
];
